import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { HasPermissionDirective } from '../../../../../directives/has-permission.directive';
import { locationDefinition } from '../../../../../entity-definitions/location.definition';
import { EntityDialogService } from '../../../../../services/entity-dialog.service';
import { ToastService } from '../../../../../services/toast.service';
import { AssetStore } from '../../../../../stores/asset.store';
import { LocationStore } from '../../../../../stores/location.store';
import { Asset } from '../../../../../types/asset.interface';
import { Location } from '../../../../../types/location.interface';
import { GenericParentGridComponent } from '../../../../generic/generic-parent-grid.component';
import { CesAssetBreadcrumbComponent } from '../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesButtonComponent } from '../../../../ui/ces-buttons';
import { CesTitleComponent } from '../../../../ui/ces-route-title/ces-route-title.component';

interface AssetStoreApi {
  byId(): (id: number) => Asset | undefined;
  loadById(id: number): Promise<Asset>;
}

interface LocationStoreApi {
  entities(): Location[];
  create(entity: Partial<Location>): Promise<Location | null>;
  update(id: number, changes: Partial<Location>): Promise<Location | null>;
  delete(id: number): Promise<void>;
}

@Component({
  selector: 'app-locations-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CesAssetBreadcrumbComponent,
    CesTitleComponent,
    GenericParentGridComponent,
    CesButtonComponent,
    HasPermissionDirective,
    TranslocoModule,
  ],
  templateUrl: './locations-list.component.html',
})
export class LocationListComponent implements OnInit, OnDestroy {
  protected readonly locationStore = inject(LocationStore) as unknown as LocationStoreApi;
  protected readonly assetStore = inject(AssetStore) as unknown as AssetStoreApi;
  private readonly route = inject(ActivatedRoute);
  private readonly dialogService = inject(EntityDialogService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translateService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);

  readonly locationDefinition = locationDefinition;
  filteredLocations: Location[] = [];
  readonly currentAssetId$ = signal<number | null>(null);
  currentAsset: Asset | null = null;

  private readonly translations = toSignal(this.translateService.selectTranslation());

  readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    this.translations();
    return [{ label: this.translateService.translate('navigation.routes.locations') }];
  });

  private readonly destroy$ = new Subject<void>();
  dataLoadingCompleted = false;

  get currentAssetId(): number | null {
    return this.currentAssetId$();
  }
  set currentAssetId(value: number | null) {
    this.currentAssetId$.set(value);
  }

  readonly createPermissionConfig = computed(() => ({
    method: 'postLocationListResource' as const,
    assetId: this.currentAssetId$() ?? undefined,
  }));

  readonly entityDefinitionWithPermissions = computed(() => {
    const assetId = this.currentAssetId$() ?? undefined;
    return {
      ...locationDefinition,
      actionButtons: {
        ...locationDefinition.actionButtons,
        parent: {
          ...locationDefinition.actionButtons?.parent,
          addPermission: { method: 'postLocationListResource' as const, assetId },
          editPermission: { method: 'putLocationDetailResource' as const, assetId },
          deletePermission: { method: 'deleteLocationDetailResource' as const, assetId },
        },
      },
    };
  });

  ngOnInit(): void {
    this.route.parent?.parent?.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['id']) {
        this.currentAssetId = Number(params['id']);
        if (this.currentAssetId <= 0) {
          console.error(this.translateService.translate('common.errors.invalidAssetId'), this.currentAssetId);
          this.currentAssetId = null;
          this.currentAsset = null;
          this.filteredLocations = [];
          this.cdr.markForCheck();
          return;
        }

        void this.loadAssetDetails(this.currentAssetId);
        void this.loadData();
        this.dataLoadingCompleted = true;
        this.cdr.markForCheck();
      } else {
        this.currentAssetId = null;
        this.currentAsset = null;
        this.filteredLocations = [];
        this.cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadAssetDetails(assetId: number): Promise<void> {
    try {
      let asset = this.assetStore.byId()(assetId);

      if (!asset) {
        await this.assetStore.loadById(assetId);
        asset = this.assetStore.byId()(assetId);
      }

      if (!asset) {
        console.error(this.translateService.translate('common.errors.assetNotFoundAfterLoad', { id: assetId }));
        this.currentAssetId = null;
        this.filteredLocations = [];
        this.cdr.markForCheck();
        return;
      }

      this.currentAsset = asset;
    } catch (error) {
      console.error(this.translateService.translate('common.errors.assetLoadFailed'), assetId, error);
      this.currentAssetId = null;
      this.currentAsset = null;
      this.filteredLocations = [];
      this.cdr.markForCheck();
    }
  }

  private async loadData(): Promise<void> {
    if (!this.currentAssetId) return;

    try {
      this.updateFilteredLocations();
    } catch (error) {
      console.error(this.translateService.translate('common.errors.locationsLoadFailed'), error);
    } finally {
      this.cdr.markForCheck();
    }
  }

  private updateFilteredLocations(): void {
    if (!this.currentAssetId) {
      this.filteredLocations = [];
      return;
    }

    this.filteredLocations = this.locationStore
      .entities()
      .filter(location => location.assetId === this.currentAssetId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  createLocation(): void {
    if (!this.currentAssetId) {
      console.error(this.translateService.translate('common.errors.noAssetSelected'));
      return;
    }

    const targetAssetId = this.currentAssetId;

    this.dialogService
      .openCreateDialog<Location>({
        header: this.locationDefinition.messages.dialogTitles.create(),
        entityDefinition: this.locationDefinition,
        mode: 'create',
        additionalData: {
          assetId: targetAssetId,
        },
      })
      .subscribe(formData => {
        if (!formData) return;
        if ('__action' in formData && formData.__action === 'delete') return;

        const payload: Partial<Location> = {
          ...(formData as Partial<Location>),
          assetId: targetAssetId,
        };

        void this.locationStore.create(payload).then(() => {
          void this.loadData();
        });
      });
  }

  editLocation(location: Location): void {
    if (!location.assetId || location.assetId <= 0) {
      console.error(this.translateService.translate('common.errors.invalidAssetIdOnLocation'), location);
      return;
    }

    this.dialogService
      .openEditDialog<Location>(location, {
        header: this.locationDefinition.messages.dialogTitles.edit(),
        headerParams: { name: location.name },
        entityDefinition: this.locationDefinition,
        mode: 'edit',
        additionalData: {
          assetId: location.assetId,
        },
        onDelete: entity => this.deleteLocation(entity),
      })
      .subscribe(result => {
        if (!result) return;
        if ('__action' in result && result.__action === 'delete') {
          this.deleteLocation(result.entity as Location);
          return;
        }

        const payload: Partial<Location> = {
          ...(result as Partial<Location>),
          assetId: location.assetId,
        };

        void this.locationStore.update(location.id, payload).then(() => {
          void this.loadData();
        });
      });
  }

  deleteLocation(location: Location): void {
    if (!location?.id) {
      console.error(this.translateService.translate('common.errors.invalidLocationDeletion'));
      return;
    }

    this.locationStore
      .delete(location.id)
      .then(() => {
        void this.loadData();
      })
      .catch((error: unknown) => {
        this.toastService.warning('entities.location.messages.deleteError');
        console.error(this.translateService.translate('common.errors.locationDeletionFailed'), error);
      });
  }
}
